import React, { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Tooltip, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import Supercluster from "supercluster";
import "leaflet/dist/leaflet.css";
import "leaflet.heat";
import { thumbUrl } from "@/lib/api";

// BASEMAP. The original map used CARTO dark_all raster tiles WITHOUT a key; CARTO then started stamping every keyless tile
// "API KEY REQUIRED", and the follow-up change rendered NO tile layer at all when REACT_APP_CARTO_BASEMAP_KEY was empty
// (black map + banner). Restored behaviour: a real basemap ALWAYS renders —
//   • CARTO dark_all when a build-time public basemap key exists (REACT_APP_CARTO_BASEMAP_KEY, browser-safe tile key), else
//   • OpenStreetMap standard tiles (keyless, attribution required) darkened with a CSS filter to keep the BareFootage dark look.
const CARTO_KEY = (process.env.REACT_APP_CARTO_BASEMAP_KEY || "").trim();
export const CARTO_TILE_URL = CARTO_KEY ? `https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png?key=${encodeURIComponent(CARTO_KEY)}` : "";
export const OSM_TILE_URL = "https://tile.openstreetmap.org/{z}/{x}/{y}.png";
export const BASEMAP = CARTO_TILE_URL ? "carto" : "osm-dark";
const OSM_ATTRIBUTION = '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">OpenStreetMap</a> contributors';
const ATTRIBUTION = OSM_ATTRIBUTION + ' &copy; <a href="https://carto.com/attributions" target="_blank" rel="noreferrer">CARTO</a>';
// dark-mode recipe for light OSM tiles: invert + hue-rotate keeps roads/labels legible while the canvas goes dark
const OSM_DARK_FILTER = "invert(1) hue-rotate(180deg) brightness(0.72) contrast(1.05) saturate(0.35)";

// VISUALISATION BY ZOOM (one dataset, never re-grouped by administrative area):
//   zoom ≤ HEAT_ONLY_MAX      → heat only (compact, zoom-scaled blobs)
//   HEAT_ONLY_MAX < z ≤ HEAT_MAX_ZOOM → heat + geographic clusters overlaid
//   z > HEAT_MAX_ZOOM         → geographic clusters that split progressively into final logical-location markers
export const HEAT_ONLY_MAX = 3;
export const HEAT_MAX_ZOOM = 5;
export const mapPhase = (z) => (z <= HEAT_ONLY_MAX ? "heat" : z <= HEAT_MAX_ZOOM ? "heat+clusters" : "clusters");
// heat radius/blur grow with zoom so a continent view shows compact density regions and a close view precise density
export const heatOptions = (z) => ({ radius: Math.round(Math.min(34, 9 + z * 2.6)), blur: Math.round(Math.min(26, 7 + z * 2)), maxZoom: 12, minOpacity: z > HEAT_ONLY_MAX ? 0.22 : 0.32 });
const HEAT_GRADIENT = { 0.2: "#3a2a00", 0.4: "#8a5a00", 0.6: "#d9a24a", 0.8: "#f2c66b", 1.0: "#ffe9a8" };

// gold heat layer — same per-media points as the clusters; radius/blur re-scaled on every zoom change
const HeatLayer = ({ points, zoom }) => {
  const map = useMap();
  const layerRef = useRef(null);
  useEffect(() => {
    const data = points.map((p) => [p.lat, p.lng, 1]);   // pure upload density: every eligible upload contributes the same heat
    const layer = L.heatLayer(data, { ...heatOptions(map.getZoom()), gradient: HEAT_GRADIENT });
    const origRedraw = layer._redraw;
    layer._redraw = function () { if (!this._map) { this._frame = null; return; } return origRedraw.call(this); };   // guard a frame that fires after removal
    layer.addTo(map);
    layerRef.current = layer;
    return () => {
      if (layer._frame) { L.Util.cancelAnimFrame(layer._frame); layer._frame = null; }   // leaflet.heat never cancels its pending redraw on remove
      map.removeLayer(layer); layerRef.current = null;
    };
  }, [map, points]);
  useEffect(() => { const l = layerRef.current; if (l && l._map) l.setOptions(heatOptions(zoom)); }, [zoom]);
  return null;
};

const ViewTracker = ({ onMove, onView }) => {
  const map = useMap();
  const emit = () => {
    const c = map.getCenter();
    onView({ zoom: map.getZoom(), bounds: map.getBounds() });
    onMove?.({ center: { lat: c.lat, lng: c.lng }, zoom: map.getZoom(), bounds: map.getBounds() });
  };
  useEffect(() => { onView({ zoom: map.getZoom(), bounds: map.getBounds() }); }, [map]); // eslint-disable-line
  useMapEvents({ moveend: emit, zoomend: emit });
  return null;
};

// react-leaflet v5 reads `center` only once; fly the map when it changes
const Recenter = ({ center, zoom }) => {
  const map = useMap();
  const prev = React.useRef(null);
  useEffect(() => {
    if (!center) return;
    const key = `${center[0]},${center[1]}`;
    if (prev.current === key) return;
    prev.current = key;
    map.flyTo(center, Math.max(map.getZoom(), zoom || 5), { duration: 0.8 });
  }, [center, zoom, map]);
  return null;
};

const clusterIcon = (c, size, kind) => {
  const s = Math.round(size);
  const isVid = c.thumb && c.thumb.media_type === "video";
  // Video map thumbs use the derived poster only; never the raw .mov. Fall back to the count bubble.
  const url = c.thumb
    ? (isVid ? (c.thumb.cloud_poster_url || "") : (c.thumb.file_ref ? thumbUrl(c.thumb.file_ref, 120) : ""))
    : "";
  const inner = url
    ? `<img src="${url}" alt="" style="width:100%;height:100%;object-fit:cover;border-radius:9999px" />`
    : `<span style="display:flex;align-items:center;justify-content:center;width:100%;height:100%;background:#141414;color:#d9a24a;font-weight:700;font-size:12px">${c.count}</span>`;
  const ring = kind === "cluster" ? "border:2px dashed #d9a24a" : "border:2px solid #d9a24a";
  return L.divIcon({
    className: "",
    html: `<div data-testid="${kind === "cluster" ? "map-cluster" : "map-location-marker"}" data-count="${c.count}" data-key="${c.key || ""}" style="width:${s}px;height:${s}px;border-radius:9999px;overflow:hidden;${ring};box-shadow:0 0 0 3px rgba(217,162,74,.22),0 3px 12px rgba(0,0,0,.6);position:relative">${inner}<span style="position:absolute;bottom:-5px;right:-5px;background:#d9a24a;color:#0a0a0a;font-size:10px;font-weight:800;line-height:1;padding:3px 5px;border-radius:9999px;border:1.5px solid #0a0a0a;box-shadow:0 1px 4px rgba(0,0,0,.5)">${c.count}</span></div>`,
    iconSize: [s, s], iconAnchor: [s / 2, s / 2],
  });
};

const pulseIcon = () => L.divIcon({
  className: "",
  html: `<span class="bf-pulse"></span>`,
  iconSize: [18, 18], iconAnchor: [9, 9],
});

const better = (a, b) => a[0] > b[0] || (a[0] === b[0] && a[1] > b[1]);
const pointThumb = (p) => (p.file_ref || p.cloud_poster_url ? { file_ref: p.file_ref, media_type: p.media_type, cloud_poster_url: p.cloud_poster_url } : null);

// Geographic clustering of the per-media points (Supercluster). A cluster whose members all share one logical location
// `key` (city) is the FINAL marker for that place (count = its uploads); a cluster mixing keys is an intermediate geographic
// cluster positioned at the mean of its members' real coordinates — never a country/region centroid.
export const buildClusterIndex = (points) => {
  const idx = new Supercluster({
    radius: 70, maxZoom: 16, minPoints: 2,
    map: (p) => ({ count: 1, key: p.key, mixed: false, name: p.city || p.country || "", country: p.country || "", best: [p.rating_count || 0, p.avg_rating || 0], thumb: pointThumb(p) }),
    reduce: (acc, p) => {
      acc.count += p.count;
      if (p.mixed || acc.key !== p.key) acc.mixed = true;
      if (better(p.best, acc.best)) { acc.best = p.best; acc.thumb = p.thumb; }
    },
  });
  idx.load(points.map((p) => ({ type: "Feature", geometry: { type: "Point", coordinates: [p.lng, p.lat] }, properties: p })));
  return idx;
};

// Deterministic representative for a single-location cluster (Iter125w): best rated; ties → the member closest to the cluster centre; then id.
// Never "first/newest member", so a city marker cannot wear one arbitrary upload's poster.
const haversineKm = (a, b, c, d) => { const r = Math.PI / 180, x = Math.sin((c - a) * r / 2), y = Math.sin((d - b) * r / 2); return 12742 * Math.asin(Math.sqrt(x * x + Math.cos(a * r) * Math.cos(c * r) * y * y)); };
export const pickRepresentative = (members, lat, lng) => {
  let best = null, bestKey = null;
  for (const p of members) {
    const k = [-(p.rating_count || 0), -(p.avg_rating || 0), haversineKm(lat, lng, p.lat, p.lng), String(p.id || "")];
    if (!best || k[0] < bestKey[0] || (k[0] === bestKey[0] && (k[1] < bestKey[1] || (k[1] === bestKey[1] && (k[2] < bestKey[2] || (k[2] === bestKey[2] && k[3] < bestKey[3])))))) { best = p; bestKey = k; }
  }
  return best ? pointThumb(best) : null;
};

// Visible features for a viewport: cluster → {kind:'cluster'|'city'}; lone point → {kind:'city', count:1}
export const visibleFeatures = (idx, bounds, zoom) => {
  if (!idx || !bounds) return [];
  const w = bounds.getWest(), e = bounds.getEast(), s = bounds.getSouth(), n = bounds.getNorth();
  const pad = Math.min(60, (e - w) * 0.15);
  const bbox = e - w >= 360 ? [-180, -85, 180, 85] : [Math.max(-180, w - pad), Math.max(-85, s - pad), Math.min(180, e + pad), Math.min(85, n + pad)];
  return idx.getClusters(bbox, Math.round(zoom)).map((f) => {
    const [lng, lat] = f.geometry.coordinates;
    const p = f.properties;
    if (p.cluster) {
      const thumb = p.mixed ? p.thumb : pickRepresentative(idx.getLeaves(p.cluster_id, Infinity).map((l) => l.properties), lat, lng);
      return { id: `c${p.cluster_id}`, clusterId: p.cluster_id, lat, lng, count: p.count, kind: p.mixed ? "cluster" : "city",
               key: p.mixed ? null : p.key, name: p.mixed ? "" : p.name, country: p.mixed ? "" : p.country, thumb };
    }
    return { id: `p${p.id}`, lat, lng, count: 1, kind: "city", key: p.key, name: p.city || p.country || "", country: p.country || "", thumb: pointThumb(p) };
  });
};

// member bounds of a cluster (its real member points), used for click → fit
export const clusterMemberBounds = (idx, clusterId) => {
  const leaves = idx.getLeaves(clusterId, Infinity);
  return L.latLngBounds(leaves.map((l) => [l.geometry.coordinates[1], l.geometry.coordinates[0]]));
};

const ClusterLayer = ({ index, view, onSelectCity }) => {
  const map = useMap();
  const feats = useMemo(() => visibleFeatures(index, view?.bounds, view?.zoom ?? 2), [index, view]);
  const maxCount = useMemo(() => Math.max(1, ...feats.map((f) => f.count)), [feats]);
  const fitCluster = (f) => {
    const b = clusterMemberBounds(index, f.clusterId);
    const fitZoom = map.getBoundsZoom(b, false, L.point(48, 48));
    const expand = Math.min(16, index.getClusterExpansionZoom(f.clusterId));
    map.flyTo(b.getCenter(), Math.max(fitZoom, expand, map.getZoom() + 1), { duration: 0.7 });   // fit real member bounds; always splits the cluster
  };
  return feats.map((f) => {
    const size = 36 + (f.count / maxCount) * 32; // 36–68px, scales with uploads
    return (
      <Marker key={f.id} position={[f.lat, f.lng]} icon={clusterIcon(f, size, f.kind)}
        eventHandlers={{ click: () => (f.kind === "cluster" ? fitCluster(f) : onSelectCity?.({ slug: f.key, city: f.name, country: f.country, count: f.count, lat: f.lat, lng: f.lng })) }}>
        <Tooltip direction="top" offset={[0, -size / 2]} opacity={1} className="bf-tip">
          {f.kind === "cluster"
            ? <><span style={{ fontWeight: 600 }}>{f.count} photos &amp; videos</span> · click to zoom in</>
            : <><span style={{ fontWeight: 600 }}>{f.name}</span> · {f.count} {f.count === 1 ? "item" : "photos & videos"} · click for all</>}
        </Tooltip>
      </Marker>
    );
  });
};

// Desktop click-drag: draw a growing circle from the press point; on release, return {lat,lng} + radius km
const DrawController = ({ active, onDone }) => {
  const map = useMap();
  const startRef = React.useRef(null);
  const circleRef = React.useRef(null);
  useEffect(() => {
    if (!active) return;
    const container = map.getContainer();
    map.dragging.disable(); map.doubleClickZoom.disable(); map.boxZoom.disable();
    container.style.cursor = "crosshair";
    const onDown = (e) => {
      startRef.current = e.latlng;
      if (circleRef.current) { map.removeLayer(circleRef.current); circleRef.current = null; }
      circleRef.current = L.circle(e.latlng, { radius: 0, color: "#d9a24a", weight: 2, fillColor: "#d9a24a", fillOpacity: 0.15, className: "bf-draw-circle" }).addTo(map);
    };
    const onMove = (e) => {
      if (!startRef.current || !circleRef.current) return;
      circleRef.current.setRadius(startRef.current.distanceTo(e.latlng));
    };
    const onUp = (e) => {
      if (!startRef.current) return;
      const c = startRef.current;
      const radiusKm = Math.max(1, Math.round(c.distanceTo(e.latlng) / 1000));
      startRef.current = null;
      onDone?.({ lat: c.lat, lng: c.lng }, radiusKm);
      setTimeout(() => { if (circleRef.current) { map.removeLayer(circleRef.current); circleRef.current = null; } }, 2500);
    };
    map.on("mousedown", onDown); map.on("mousemove", onMove); map.on("mouseup", onUp);
    return () => {
      map.off("mousedown", onDown); map.off("mousemove", onMove); map.off("mouseup", onUp);
      map.dragging.enable(); map.doubleClickZoom.enable(); map.boxZoom.enable();
      container.style.cursor = "";
      if (circleRef.current) { map.removeLayer(circleRef.current); circleRef.current = null; }
    };
  }, [active, map, onDone]);
  return null;
};

export const MapView = ({ points = [], pulses = [], center = [20, 10], zoom = 2, flyTo = null, flyZoom = 6, drawMode = false, onDrawArea, onSelectCity, onSelectMedia, onMove }) => {
  const [view, setView] = useState(null);
  const index = useMemo(() => buildClusterIndex(points), [points]);
  const z = view?.zoom ?? zoom;
  const phase = mapPhase(z);
  return (
    <div data-testid="leaflet-map" data-tiles={BASEMAP} data-phase={phase} data-zoom={z} data-points={points.length} style={{ height: "100%", width: "100%", position: "relative" }}>
    <style>{`.bf-osm-dark { filter: ${OSM_DARK_FILTER}; }`}</style>
    <MapContainer center={center} zoom={zoom} minZoom={2} maxZoom={16} worldCopyJump
      style={{ height: "100%", width: "100%", background: "#0a0a0a" }} zoomControl={true} attributionControl={true}>
      {CARTO_TILE_URL
        ? <TileLayer url={CARTO_TILE_URL} attribution={ATTRIBUTION} subdomains="abcd" maxZoom={19} />
        : <TileLayer url={OSM_TILE_URL} attribution={OSM_ATTRIBUTION} maxZoom={19} className="bf-osm-dark" />}
      <ViewTracker onMove={onMove} onView={setView} />
      <Recenter center={flyTo} zoom={flyZoom} />
      <DrawController active={drawMode} onDone={onDrawArea} />

      {phase !== "clusters" && <HeatLayer points={points} zoom={z} />}
      {phase !== "heat" && <ClusterLayer index={index} view={view} onSelectCity={onSelectCity} />}

      {pulses.map((p) => (
        <Marker key={p.key} position={[p.lat, p.lng]} icon={pulseIcon()} eventHandlers={{ click: () => onSelectMedia?.(p) }}>
          <Tooltip direction="top" offset={[0, -6]} opacity={1} className="bf-tip" permanent>
            <span style={{ fontWeight: 600 }}>New</span> · {p.city}
          </Tooltip>
        </Marker>
      ))}
    </MapContainer>
    </div>
  );
};
